<template>
    <MultiSelect
        v-model="selectedConnectors"
        :options="connectorOptions"
        option-label="label"
        track-by="value"
        :placeholder="placeholder"
        :disabled="loading || disabled"
        :show-tags="showTags"
        :max-count="maxCount"
        :class="containerClass"
        :style="{ width: width }"
        :enable-refresh="enableRefresh"
        :refresh-loading="refreshLoading"
        :modal-popover="true"
        :show-select-all="false"
        :disabled-options="disabledOptions"
        :as-child="enableFilterIcon"
        @update:model-value="handleSelectionChange"
        @refresh="$emit('refresh')">
        <template v-if="enableFilterIcon">
            <Button
                variant="outline"
                :disabled="loading || disabled"
                :style="{ width: width }"
                class="h-9 inline-flex w-full justify-start items-center px-2 text-sm font-medium min-w-40">
                <Filter :class="['h-4 w-4 mr-1', hasSelection ? 'text-primary' : '']" :fill="hasSelection ? 'currentColor' : 'none'" />
                <span class="pl-0.5">
                    {{ hasSelection ? `${selectedConnectors.length} selected` : placeholder }}
                </span>
                <ChevronDown class="-mr-1 ml-2 h-5 w-5" />
            </Button>
        </template>

        <template #option="{ option }">
            <div class="flex gap-2 items-center">
                <Avatar
                    v-if="option.connectorType && option.connectorType !== 'All'"
                    class="w-5 h-5 bg-background dark:bg-slate-200 border border-border rounded-sm flex-shrink-0">
                    <AvatarImage :src="`/icons/${option.connectorType}.svg`" :alt="option.label" class="object-contain p-0.5" />
                    <AvatarFallback class="bg-background dark:bg-slate-200 dark:text-slate-800 text-muted-foreground text-xs rounded-sm">
                        {{ option.connectorType?.charAt(0).toUpperCase() }}
                    </AvatarFallback>
                </Avatar>
                <component v-else-if="getDefaultIcon(option)" :is="getDefaultIcon(option)" class="w-5 h-5 flex-shrink-0" />
                <span class="truncate">{{ option.label }}</span>
            </div>
        </template>

        <template #tag="{ option, onRemove }">
            <div class="flex items-center gap-1 bg-muted px-2 py-1 rounded text-sm">
                <Avatar
                    v-if="option.connectorType && option.connectorType !== 'All'"
                    class="w-4 h-4 bg-background dark:bg-slate-200 border border-border rounded-sm flex-shrink-0">
                    <AvatarImage :src="`/icons/${option.connectorType}.svg`" :alt="option.label" class="object-contain p-0.5" />
                    <AvatarFallback class="bg-background dark:bg-slate-200 dark:text-slate-800 text-muted-foreground text-xs rounded-sm">
                        {{ option.connectorType?.charAt(0).toUpperCase() }}
                    </AvatarFallback>
                </Avatar>
                <component v-else-if="getDefaultIcon(option)" :is="getDefaultIcon(option)" class="w-4 h-4 flex-shrink-0" />
                <span class="truncate max-w-24">{{ option.label }}</span>
                <Button variant="ghost" size="icon" class="ml-1 h-4 w-4 p-0 text-muted-foreground hover:text-muted-foreground" @click="onRemove">
                    <X class="w-3 h-3" />
                </Button>
            </div>
        </template>
    </MultiSelect>
</template>

<script setup>
import { computed } from 'vue';
import { MultiSelect } from '@/components/ui/multi-select';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Server, Database, CloudDownload, CloudUpload, X, Filter, ChevronDown } from 'lucide-vue-next';

const props = defineProps({
    modelValue: {
        type: Array,
        default: () => []
    },
    connectors: {
        type: Array,
        required: true
    },
    placeholder: {
        type: String,
        default: 'Select connectors...'
    },
    loading: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    showTags: {
        type: Boolean,
        default: true
    },
    maxCount: {
        type: Number,
        default: 3
    },
    width: {
        type: String,
        default: 'auto'
    },
    containerClass: {
        type: String,
        default: ''
    },
    enableRefresh: {
        type: Boolean,
        default: false
    },
    refreshLoading: {
        type: Boolean,
        default: false
    },
    includeAllOption: {
        type: Boolean,
        default: true
    },
    enableFilterIcon: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:modelValue', 'refresh']);

const selectedConnectors = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value)
});

const hasSelection = computed(() => {
    return props.modelValue && props.modelValue.length > 0 && !props.modelValue.includes('All');
});

const connectorOptions = computed(() => {
    const options = [];

    // Add "All Connectors" option if enabled
    if (props.includeAllOption) {
        options.push({
            value: 'All',
            label: 'All Connectors',
            connectorType: 'All'
        });
    }

    // Add actual connectors
    if (props.connectors?.length) {
        const formattedConnectors = props.connectors.map((connector) => ({
            value: connector.id, // Use connector.id as the value
            label: connector.name || connector.connectorDisplayName,
            connectorType: connector.connectorType,
            context: connector.context,
            id: connector.id
        }));

        options.push(...formattedConnectors);
    }

    return options;
});

const disabledOptions = computed(() => {
    // Don't disable any options - let users click freely
    // The selection logic in handleSelectionChange will manage the mutual exclusivity
    return [];
});

const handleSelectionChange = (newSelection) => {
    const prevSelection = props.modelValue;
    const hasAll = newSelection.includes('All');
    const hasOthers = newSelection.some((item) => item !== 'All');
    const prevHasAll = prevSelection.includes('All');

    let finalSelection;

    if (hasAll && hasOthers) {
        // Determine what was just clicked
        if (prevHasAll) {
            // "All" was previously selected, user clicked another option
            // Remove "All" and keep only the newly selected non-All options
            finalSelection = newSelection.filter((item) => item !== 'All');
        } else {
            // Some items were selected, user clicked "All"
            // Remove everything else and keep only "All"
            finalSelection = ['All'];
        }
    } else if (hasAll) {
        // Only "All" is selected
        finalSelection = ['All'];
    } else if (newSelection.length === 0) {
        // Nothing is selected (after clear)
        if (props.includeAllOption) {
            // If "All" option is available, default to "All"
            finalSelection = ['All'];
        } else {
            // If no "All" option, keep empty selection (show all items)
            finalSelection = [];
        }
    } else {
        // Only non-All items are selected
        finalSelection = newSelection.filter((item) => item !== 'All');
    }

    emit('update:modelValue', finalSelection);
};

const getDefaultIcon = (option) => {
    if (option.connectorType === 'All') {
        return Database;
    }

    if (option.context === 'source') {
        return CloudDownload;
    }

    if (option.context === 'destination') {
        return CloudUpload;
    }

    return Server;
};

const onImageError = (event) => {
    event.target.style.display = 'none';
};
</script>
